/**
 * XO Club - WebSocket Hook
 * ========================
 * Gestion de la connexion WebSocket pour les mises à jour en temps réel.
 * Remplace les 3 boucles setInterval (notifications, stats, presence).
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const WS_RECONNECT_DELAY = 3000;
const WS_HEARTBEAT_INTERVAL = 30000;

export function useWebSocket(onMessage) {
  const { user } = useAuth();
  const ws = useRef(null);
  const reconnectTimer = useRef(null);
  const heartbeatTimer = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [liveStats, setLiveStats] = useState({ 
    playersOnline: 0, 
    openMatches: 0, 
    activeTournaments: 0, 
    matchesToday: 0 
  });

  const getToken = useCallback(() => {
    // Get token from cookies or localStorage
    const cookies = document.cookie.split(';');
    const tokenCookie = cookies.find(c => c.trim().startsWith('access_token='));
    if (tokenCookie) {
      return tokenCookie.split('=')[1];
    }
    return localStorage.getItem('access_token');
  }, []);

  const connect = useCallback(() => {
    if (!user) return;
    
    const token = getToken();
    if (!token) {
      logger.debug('[WebSocket] No token available');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
      
      ws.current = new WebSocket(`${wsUrl}/ws/${token}`);

      ws.current.onopen = () => {
        logger.log('[WebSocket] Connected');
        setIsConnected(true);
        
        // Start heartbeat
        heartbeatTimer.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, WS_HEARTBEAT_INTERVAL);
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle built-in message types
          switch (data.type) {
            case 'init':
              setUnreadCount(data.data?.unreadNotifications || 0);
              break;
            case 'unread_count':
              setUnreadCount(data.data?.count || 0);
              break;
            case 'live_stats':
              setLiveStats(data.data);
              break;
            case 'notification':
              setUnreadCount(prev => prev + 1);
              break;
            case 'new_message':
              // New message received - handled by onMessage callback
              break;
            case 'match_update':
              // Match status update - handled by onMessage callback
              break;
            case 'invitation':
              // New invitation - handled by onMessage callback
              break;
            case 'pong':
              // Heartbeat acknowledged
              break;
            default:
              break;
          }
          
          // Call custom handler for all messages
          if (onMessage) {
            onMessage(data);
          }
        } catch (e) {
          logger.error('[WebSocket] Failed to parse message:', e);
        }
      };

      ws.current.onclose = (event) => {
        logger.debug('[WebSocket] Disconnected:', event.code);
        setIsConnected(false);
        clearInterval(heartbeatTimer.current);
        
        // Reconnect with delay (unless intentionally closed)
        if (event.code !== 1000) {
          reconnectTimer.current = setTimeout(connect, WS_RECONNECT_DELAY);
        }
      };

      ws.current.onerror = (error) => {
        logger.error('[WebSocket] Error:', error);
      };
    } catch (e) {
      logger.error('[WebSocket] Connection failed:', e);
      reconnectTimer.current = setTimeout(connect, WS_RECONNECT_DELAY);
    }
  }, [user, getToken, onMessage]);

  const disconnect = useCallback(() => {
    if (ws.current) {
      ws.current.close(1000);
      ws.current = null;
    }
    clearTimeout(reconnectTimer.current);
    clearInterval(heartbeatTimer.current);
    setIsConnected(false);
  }, []);

  const send = useCallback((type, data = {}) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ type, ...data }));
    }
  }, []);

  const requestUnreadCount = useCallback(() => {
    send('get_unread');
  }, [send]);

  const requestLiveStats = useCallback(() => {
    send('get_live_stats');
  }, [send]);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    isConnected,
    unreadCount,
    liveStats,
    send,
    requestUnreadCount,
    requestLiveStats,
    disconnect
  };
}

export default useWebSocket;
